/**
 * Completion Command
 * Generate shell completions for bash, zsh, and fish
 */

import { Command } from 'commander';

const COMMANDS = [
    'agent', 'login', 'logout', 'tasks', 'context', 'serve', 'validate',
    'trace', 'gateway', 'prompt', 'ls', 'resume', 'show', 'star', 'rename',
    'delete', 'archive', 'devices', 'config', 'mcp', 'sparklet', 'completion', 'update',
];

const GLOBAL_OPTIONS = [
    '--config', '--no-mcp', '--no-color', '--debug', '--message', '--resume',
    '--gateway', '--session-name', '--version', '--help',
];

function generateBashCompletion(): string {
    return `# bash completion for paean CLI
# Add to ~/.bashrc or ~/.bash_profile:
#   eval "$(paean completion bash)"

_paean_completion() {
    local cur prev commands global_opts
    COMPREPLY=()
    cur="\${COMP_WORDS[COMP_CWORD]}"
    prev="\${COMP_WORDS[COMP_CWORD-1]}"
    commands="${COMMANDS.join(' ')}"
    global_opts="${GLOBAL_OPTIONS.join(' ')}"

    case "\${prev}" in
        paean)
            COMPREPLY=( $(compgen -W "\${commands} \${global_opts}" -- "\${cur}") )
            return 0
            ;;
        tasks)
            COMPREPLY=( $(compgen -W "add complete update delete pending accept reject partial" -- "\${cur}") )
            return 0
            ;;
        config)
            COMPREPLY=( $(compgen -W "show set reset path" -- "\${cur}") )
            return 0
            ;;
        mcp)
            COMPREPLY=( $(compgen -W "list add remove test cloud" -- "\${cur}") )
            return 0
            ;;
        sparklet)
            COMPREPLY=( $(compgen -W "list show open delete publish" -- "\${cur}") )
            return 0
            ;;
        devices)
            COMPREPLY=( $(compgen -W "revoke revoke-all" -- "\${cur}") )
            return 0
            ;;
    esac

    if [[ "\${cur}" == -* ]]; then
        COMPREPLY=( $(compgen -W "\${global_opts}" -- "\${cur}") )
        return 0
    fi

    COMPREPLY=( $(compgen -W "\${commands}" -- "\${cur}") )
}
complete -F _paean_completion paean
`;
}

function generateZshCompletion(): string {
    return `#compdef paean
# zsh completion for paean CLI
# Add to ~/.zshrc:
#   eval "$(paean completion zsh)"

_paean() {
    local -a commands
    commands=(
${COMMANDS.map(c => `        '${c}:${c} command'`).join('\n')}
    )

    local -a global_opts
    global_opts=(
        '--config[Show config file path]'
        '--no-mcp[Disable MCP integration]'
        '--no-color[Disable colored output]'
        '-d[Enable debug logging]'
        '--debug[Enable debug logging]'
        '-m[Send a single message]:message:'
        '--message[Send a single message]:message:'
        '-r[Resume conversation]:id:'
        '--resume[Resume conversation]:id:'
        '-v[Show version]'
        '--version[Show version]'
        '-h[Show help]'
        '--help[Show help]'
    )

    _arguments -C \\
        '1:command:->command' \\
        '*::arg:->args'

    case "$state" in
        command)
            _describe 'command' commands
            _values 'options' $global_opts
            ;;
        args)
            case "$words[1]" in
                tasks)
                    local -a task_cmds=('add:Create task' 'complete:Complete task' 'update:Update task' 'delete:Delete task' 'pending:View pending' 'accept:Accept change' 'reject:Reject change' 'partial:Partial accept')
                    _describe 'tasks command' task_cmds
                    ;;
                config)
                    local -a config_cmds=('show:Show config' 'set:Set value' 'reset:Reset config' 'path:Show path')
                    _describe 'config command' config_cmds
                    ;;
                mcp)
                    local -a mcp_cmds=('list:List servers' 'add:Add server' 'remove:Remove server' 'test:Test connection' 'cloud:List cloud configs')
                    _describe 'mcp command' mcp_cmds
                    ;;
                sparklet)
                    local -a sparklet_cmds=('list:List sparklets' 'show:Show details' 'open:Open in browser' 'delete:Delete sparklet' 'publish:Publish sparklet')
                    _describe 'sparklet command' sparklet_cmds
                    ;;
                devices)
                    local -a device_cmds=('revoke:Revoke session' 'revoke-all:Revoke all others')
                    _describe 'devices command' device_cmds
                    ;;
            esac
            ;;
    esac
}

_paean "$@"
`;
}

function generateFishCompletion(): string {
    return `# fish completion for paean CLI
# Save to ~/.config/fish/completions/paean.fish

# Main commands
${COMMANDS.map(c => `complete -c paean -n '__fish_use_subcommand' -a '${c}' -d '${c} command'`).join('\n')}

# Global options
complete -c paean -l config -d 'Show config file path'
complete -c paean -l no-mcp -d 'Disable MCP integration'
complete -c paean -l no-color -d 'Disable colored output'
complete -c paean -s d -l debug -d 'Enable debug logging'
complete -c paean -s m -l message -d 'Send a single message' -r
complete -c paean -s r -l resume -d 'Resume conversation' -r
complete -c paean -s v -l version -d 'Show version'
complete -c paean -s h -l help -d 'Show help'

# Tasks subcommands
complete -c paean -n '__fish_seen_subcommand_from tasks' -a 'add' -d 'Create task'
complete -c paean -n '__fish_seen_subcommand_from tasks' -a 'complete' -d 'Complete task'
complete -c paean -n '__fish_seen_subcommand_from tasks' -a 'update' -d 'Update task'
complete -c paean -n '__fish_seen_subcommand_from tasks' -a 'delete' -d 'Delete task'
complete -c paean -n '__fish_seen_subcommand_from tasks' -a 'pending' -d 'View pending'
complete -c paean -n '__fish_seen_subcommand_from tasks' -a 'accept' -d 'Accept change'
complete -c paean -n '__fish_seen_subcommand_from tasks' -a 'reject' -d 'Reject change'
complete -c paean -n '__fish_seen_subcommand_from tasks' -a 'partial' -d 'Partial accept'

# Config subcommands
complete -c paean -n '__fish_seen_subcommand_from config' -a 'show' -d 'Show config'
complete -c paean -n '__fish_seen_subcommand_from config' -a 'set' -d 'Set value'
complete -c paean -n '__fish_seen_subcommand_from config' -a 'reset' -d 'Reset config'
complete -c paean -n '__fish_seen_subcommand_from config' -a 'path' -d 'Show path'

# MCP subcommands
complete -c paean -n '__fish_seen_subcommand_from mcp' -a 'list' -d 'List servers'
complete -c paean -n '__fish_seen_subcommand_from mcp' -a 'add' -d 'Add server'
complete -c paean -n '__fish_seen_subcommand_from mcp' -a 'remove' -d 'Remove server'
complete -c paean -n '__fish_seen_subcommand_from mcp' -a 'test' -d 'Test connection'
complete -c paean -n '__fish_seen_subcommand_from mcp' -a 'cloud' -d 'List cloud configs'

# Sparklet subcommands
complete -c paean -n '__fish_seen_subcommand_from sparklet' -a 'list' -d 'List sparklets'
complete -c paean -n '__fish_seen_subcommand_from sparklet' -a 'show' -d 'Show details'
complete -c paean -n '__fish_seen_subcommand_from sparklet' -a 'open' -d 'Open in browser'
complete -c paean -n '__fish_seen_subcommand_from sparklet' -a 'delete' -d 'Delete sparklet'
complete -c paean -n '__fish_seen_subcommand_from sparklet' -a 'publish' -d 'Publish sparklet'

# Devices subcommands
complete -c paean -n '__fish_seen_subcommand_from devices' -a 'revoke' -d 'Revoke session'
complete -c paean -n '__fish_seen_subcommand_from devices' -a 'revoke-all' -d 'Revoke all others'
`;
}

export const completionCommand = new Command('completion')
    .description('Generate shell completions')
    .argument('<shell>', 'Shell type: bash, zsh, or fish')
    .action((shell: string) => {
        switch (shell.toLowerCase()) {
            case 'bash':
                console.log(generateBashCompletion());
                break;
            case 'zsh':
                console.log(generateZshCompletion());
                break;
            case 'fish':
                console.log(generateFishCompletion());
                break;
            default:
                console.error(`Unsupported shell: ${shell}. Use: bash, zsh, or fish`);
                process.exit(1);
        }
    });
